import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Trans, useLingui } from "@lingui/react/macro"
import { useQuery } from "@tanstack/react-query"
import { Loader2 } from "lucide-react"
import axios from "../../../lib/axios"
import { useAuth } from "../../../context/AuthProvider"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,} from "../../../components/ui/dialog"
import { Select, SelectTrigger, SelectValue, SelectContent, SelectGroup, SelectItem,} from "../../../components/ui/select"
import { Textarea } from "../../../components/ui/textarea"
import { Button } from "../../../components/ui/button"

type QuestionType = "FREETEXT" | "DROPDOWN"

type AnswerTranslation = { languagecode: string; answer: string }
type AnswerOption = { answer: AnswerTranslation[] }
type AnswersField = { answers: AnswerOption[] }

// Shape returned by GET klantcheckid?dbid=&klantcheckid=
type KlantcheckResponse = {
    klantcheckid: number
    DESCRIPTION: string
    IsActive: boolean
    QuestionNL: string
    QuestionFR: string
    QuestionEN: string
    QuestionDE: string
    QuestionType: QuestionType
    // null for FREETEXT; may arrive as an object or as a JSON string
    Answers: AnswersField | string | null
}

interface QuestionProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    klantcheckid: number
    onSubmit: (klantcheckid: number, answer: string) => void
}

// i18n.language ('nl'|'fr'|'en'|'de', possibly 'en-US') -> languagecode ('NL'|'FR'|'EN'|'DE')
function toLanguageCode(language: string): string {
    return (language || "en").slice(0, 2).toUpperCase()
}

// Answers can come back as an object or a JSON string; tolerate both and never throw.
function parseAnswerOptions(raw: AnswersField | string | null): AnswerOption[] {
    if (!raw) return []
    try {
        const obj = typeof raw === "string" ? (JSON.parse(raw) as AnswersField) : raw
        return obj?.answers ?? []
    } catch {
        return []
    }
}

export default function Questions({ open, onOpenChange, klantcheckid, onSubmit }: QuestionProps) {
    const { i18n } = useTranslation()
    const { t } = useLingui()
    const { auth } = useAuth()
    const [answer, setAnswer] = useState("")

    const dbid = auth.data?.databases?.[0]?.dbid
    const lang = toLanguageCode(i18n.language)

    const { data: question, isLoading } = useQuery<KlantcheckResponse>({
        queryKey: ["klantcheck", klantcheckid, dbid],
        queryFn: async () => {
            const { data } = await axios.get(`klantcheck?dbid=${dbid}&klantcheckid=${klantcheckid}`)
            return data
        },
        enabled: open && !!dbid && klantcheckid > 0,
    })

    const questionText = !question
        ? ""
        : lang === "NL" ? question.QuestionNL
        : lang === "FR" ? question.QuestionFR
        : lang === "DE" ? question.QuestionDE
        : question.QuestionEN

    // Build dropdown options: each option's label is its text in the current page language
    // (falling back to EN, then the first available translation). The value is the index so
    // duplicate labels can't collide; the label is what gets submitted.
    const options = useMemo(() => {
        return parseAnswerOptions(question?.Answers ?? null).map((opt, i) => {
            const translation =
                opt.answer.find((a) => a.languagecode === lang) ??
                opt.answer.find((a) => a.languagecode === "EN") ??
                opt.answer[0]
            return { value: String(i), label: translation?.answer ?? "" }
        })
    }, [question, lang])

    const isDropdown = question?.QuestionType === "DROPDOWN"

    function handleSubmit() {
        const finalAnswer = isDropdown
            ? options.find((o) => o.value === answer)?.label ?? ""
            : answer
        onSubmit(klantcheckid, finalAnswer)
        onOpenChange(false)
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{questionText}</DialogTitle>
                </DialogHeader>

                {isLoading ? (
                    <div className="flex justify-center py-6">
                        <Loader2 className="w-5 h-5 animate-spin" />
                    </div>
                ) : isDropdown ? (
                    <Select value={answer} onValueChange={setAnswer}>
                        <SelectTrigger>
                            <SelectValue placeholder={t`Kies een antwoord`} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectGroup>
                                {options.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>
                                        {o.label}
                                    </SelectItem>
                                ))}
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                ) : (
                    <Textarea
                        value={answer}
                        onChange={(e) => setAnswer(e.currentTarget.value)}
                        placeholder={t`Typ uw antwoord`}
                    />
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        <Trans>Annuleren</Trans>
                    </Button>
                    <Button onClick={handleSubmit} disabled={isLoading || !answer.trim()}>
                        <Trans>Opslaan</Trans>
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
